<template>
    <form-modal
        ref="formModal"
        :title="modalTitle"
        size="lg"
        :handle-open="onModalOpen"
    >
        <template v-slot:form>
            <div class="insight-records-modal__body">
                <div v-if="loading" class="insight-records-modal__loading">
                    <div class="insight-records-modal__skeleton-header">
                        <b-skeleton type="input" width="100%" height="38px" />
                        <b-skeleton type="button" width="90px" height="38px" />
                    </div>
                    <div class="insight-records-modal__skeleton-table">
                        <b-skeleton-table
                            :rows="6"
                            :columns="skeletonColumns"
                            :table-props="{ hover: true }"
                        />
                    </div>
                </div>
                <p v-else-if="loadError" class="insight-records-modal__error">
                    {{ errorText }}
                </p>
                <template v-else>
                    <!-- Fixed header: search (left) + export (right), no scroll -->
                    <div v-if="items.length > 0" class="insight-records-modal__header">
                        <div class="insight-records-modal__search-wrap">
                            <filter-bar
                                :filter-text="searchQuery"
                                @input="searchQuery = $event"
                            />
                        </div>
                        <b-button
                            class="btn btn-pdv btn-pdv-blue insight-records-modal__export-btn"
                            :disabled="loading || filteredItems.length === 0"
                            @click="exportExcel"
                        >
                            <i class="fe-download mr-1"></i>
                            {{ exportLabel }}
                        </b-button>
                    </div>
                    <!-- Scrollable area: table only (empty states and table body) -->
                    <div class="insight-records-modal__scroll">
                        <template v-if="items.length > 0">
                            <p v-if="hasSearchQuery && filteredItems.length === 0" class="insight-records-modal__empty">
                                {{ noRecordsFoundText }}
                            </p>
                            <div v-else class="insight-records-modal__table-wrapper">
                                <data-mode-vuetable-pagination
                                    ref="vuetable"
                                    :value="filteredItems"
                                    :fields="vuetableFields"
                                    :per-page="perPage"
                                >
                                    <template v-for="(field, idx) in tableFields" v-slot:[field.key]="props">
                                        <span :key="idx">{{ props.rowData[field.key] ?? '—' }}</span>
                                    </template>
                                </data-mode-vuetable-pagination>
                            </div>
                        </template>
                        <p v-else class="insight-records-modal__empty">
                            {{ emptyText }}
                        </p>
                    </div>
                </template>
            </div>
        </template>
    </form-modal>
</template>

<script>
import FormModal from '@/src/components/FormModal.vue'
import FilterBar from '@/src/components/FilterBar.vue'
import DataModeVuetablePagination from '@/src/components/DataModeVuetablePagination.vue'
import InsightsAlertsService from '@src/services/insights/InsightsAlertsService'
import { isInsightRequestFresh } from '@/src/components/dashboards/insight-records-request-guard'
import moment from 'moment'

const USER_INSIGHT_KEYS = [
    'promoters_without_roteiro_next_3_days',
    'users_inactive_mobile_7_days',
]

const STORE_INSIGHT_KEYS = [
    'stores_not_visited_last_week_without_roteiro',
    'stores_not_visited_this_week_with_roteiro',
]

const i18nCommon = 'COMMON'
const i18nInsights = 'INSIGHTS'
const DEFAULT_PER_PAGE = 15

export default {
    name: 'InsightRecordsModal',
    components: {
        FormModal,
        FilterBar,
        DataModeVuetablePagination,
    },
    data() {
        return {
            insightKey: null,
            modalTitle: '',
            count: 0,
            initialItems: [],
            filterParams: {},
            items: [],
            loading: false,
            loadError: false,
            searchQuery: '',
            perPage: DEFAULT_PER_PAGE,
            // Monotonic token used to ignore stale async responses.
            loadRequestToken: 0,
        }
    },
    computed: {
        isUserType() {
            return this.insightKey && USER_INSIGHT_KEYS.includes(this.insightKey)
        },
        isStoreType() {
            return this.insightKey && STORE_INSIGHT_KEYS.includes(this.insightKey)
        },
        tableFields() {
            if (this.isUserType) {
                return [
                    { key: 'name', label: this.getI18n(i18nCommon, 'name') || 'Nome' },
                    { key: 'email', label: this.getI18n(i18nCommon, 'email') || 'E-mail' },
                ]
            }
            if (this.isStoreType) {
                return [
                    { key: 'name', label: this.getI18n(i18nCommon, 'name') || 'Nome' },
                    { key: 'external_code', label: this.getI18n('POS', 'FIELDS.external_code') || 'Código externo' },
                    { key: 'regional', label: this.getI18n(i18nCommon, 'regional') || 'Regional' },
                ]
            }
            return []
        },
        /** Column headings for Excel export; match PHP exports (UserExport, PointOfSaleExport/CostServingExport). */
        exportHeadings() {
            if (this.isUserType) {
                return [
                    { key: 'name', heading: 'Nome do usuário' },
                    { key: 'email', heading: 'Email' },
                ]
            }
            if (this.isStoreType) {
                return [
                    { key: 'name', heading: 'Nome do PDV' },
                    { key: 'external_code', heading: 'Código do PDV' },
                    { key: 'regional', heading: 'Regional' },
                    {
                        key: 'last_visit_at',
                        heading: this.getI18n(i18nInsights, 'EXPORT.last_visit_date'),
                    },
                ]
            }
            return []
        },
        vuetableFields() {
            return this.tableFields.map((f) => ({
                name: f.key,
                title: f.label,
                sortField: f.key,
            }))
        },
        filteredItems() {
            const q = (this.searchQuery || '').trim().toLowerCase()
            if (!q) return this.items
            return this.items.filter((row) => {
                return this.tableFields.some((f) => {
                    const v = row[f.key]
                    return v != null && String(v).toLowerCase().includes(q)
                })
            })
        },
        hasSearchQuery() {
            return (this.searchQuery || '').trim().length > 0
        },
        skeletonColumns() {
            const n = this.tableFields.length
            return n > 0 ? n : 3
        },
        displayCountText() {
            if (this.items.length === 0) return null
            const label = this.getI18n(i18nInsights, 'LABELS.count') || 'registro(s)'
            if (this.hasSearchQuery) {
                return `${this.filteredItems.length} ${label}`
            }
            if (this.count === 0) return null
            return `${this.count} ${label}`
        },
        searchPlaceholder() {
            return this.getI18n('PLACEHOLDERS', 'search') || this.getI18n(i18nCommon, 'search') || 'Buscar...'
        },
        noRecordsFoundText() {
            return this.getI18n('PAGINATION', 'no_records_found') || 'Nenhum registro encontrado'
        },
        loadingText() {
            return this.getI18n(i18nInsights, 'MESSAGES.loading') || 'Carregando...'
        },
        errorText() {
            return this.getI18n(i18nInsights, 'MESSAGES.error') || 'Erro ao carregar os dados.'
        },
        emptyText() {
            return this.getI18n(i18nInsights, 'MESSAGES.empty') || 'Nenhum registro.'
        },
        exportLabel() {
            return this.getI18n(i18nCommon, 'export') || 'Exportar'
        },
    },
    watch: {
        searchQuery() {
            this.$nextTick(() => {
                if (this.$refs.vuetable && this.$refs.vuetable.refresh) {
                    this.$refs.vuetable.refresh()
                }
            })
        },
    },
    methods: {
        showModal({ insightKey, title, count = 0, initialItems = [], filterParams = {} }) {
            // Invalidate any in-flight request from previous modal state.
            this.loadRequestToken += 1
            this.insightKey = insightKey
            this.modalTitle = title || ''
            this.count = count
            this.initialItems = Array.isArray(initialItems) ? initialItems : []
            this.filterParams = filterParams || {}
            this.items = []
            this.searchQuery = ''
            this.loadError = false
            this.$refs.formModal.show()
        },
        onModalOpen() {
            this.loadFullList()
        },
        async loadFullList() {
            if (!this.insightKey) return
            const requestToken = this.loadRequestToken
            const requestedInsightKey = this.insightKey
            this.loading = true
            this.loadError = false
            try {
                const { data } = await InsightsAlertsService.fetchAlertDetail(this.insightKey, this.filterParams)
                if (
                    !isInsightRequestFresh({
                        requestToken,
                        currentToken: this.loadRequestToken,
                        requestedInsightKey,
                        currentInsightKey: this.insightKey,
                    })
                ) return
                const list = Array.isArray(data?.items) ? data.items : []
                this.items = list
                if (typeof data?.count === 'number') {
                    this.count = data.count
                }
            } catch (e) {
                if (
                    !isInsightRequestFresh({
                        requestToken,
                        currentToken: this.loadRequestToken,
                        requestedInsightKey,
                        currentInsightKey: this.insightKey,
                    })
                ) return
                this.loadError = true
                this.items = [...this.initialItems]
            } finally {
                if (requestToken === this.loadRequestToken) {
                    this.loading = false
                }
            }
        },
        /**
         * API sends last_visit_at as Y-m-d; Excel export uses Brazilian calendar date (dd/mm/yyyy).
         */
        formatLastVisitForExport(value) {
            if (value == null || value === '') return ''
            const m = moment(value, 'YYYY-MM-DD', true)
            return m.isValid() ? m.format('DD/MM/YYYY') : String(value)
        },
        async exportExcel() {
            const list = this.filteredItems
            if (list.length === 0) return
            const fields = this.exportHeadings
            const rows = list.map((item) => {
                const row = {}
                fields.forEach((f) => {
                    const raw = item[f.key]
                    if (f.key === 'last_visit_at') {
                        row[f.heading] = this.formatLastVisitForExport(raw)
                    } else {
                        row[f.heading] = raw ?? ''
                    }
                })
                return row
            })
            const mod = await import('xlsx')
            const XLSX = mod.default || mod
            const ws = XLSX.utils.json_to_sheet(rows)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Dados')
            const filename = `insight-${this.insightKey}-${new Date().toISOString().slice(0, 10)}.xlsx`
            XLSX.writeFile(wb, filename)
        },
    },
}
</script>

<style scoped>
/* Body: flex column so header stays fixed and only table area scrolls; responsive height */
.insight-records-modal__body {
    display: flex;
    flex-direction: column;
    min-height: min(360px, 55vh);
    max-height: min(90vh, 900px);
}

.insight-records-modal__loading {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
}

.insight-records-modal__skeleton-header {
    display: flex;
    flex-shrink: 0;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
}

.insight-records-modal__skeleton-header .b-skeleton:first-child {
    flex: 1 1 auto;
    min-width: 0;
}

.insight-records-modal__skeleton-table {
    flex: 1 1 0;
    min-height: 200px;
}

.insight-records-modal__error {
    padding: 2rem 1rem;
    margin: 0;
    color: #6c757d;
    text-align: center;
}

.insight-records-modal__error {
    color: #dc3545;
}

/* Fixed header: search (left) + export (right), same line */
.insight-records-modal__header {
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.insight-records-modal__search-wrap {
    flex: 1 1 auto;
    min-width: 200px;
    max-width: 100%;
}

.insight-records-modal__search-wrap ::v-deep .filter-bar {
    width: 100%;
    max-width: 100%;
}

.insight-records-modal__export-btn {
    flex-shrink: 0;
}

/* Scrollable area: only the table scrolls; pagination row stays fixed at bottom (like footer) */
.insight-records-modal__scroll {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 280px;
    overflow: hidden;
}

.insight-records-modal__table-wrapper {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 200px;
}

/* Vuetable root: flex column so table scrolls and pagination row stays fixed */
.insight-records-modal__table-wrapper ::v-deep > div {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 200px;
}

.insight-records-modal__table-wrapper ::v-deep .table-responsive {
    flex: 1 1 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: auto;
}

/* Sticky table header: stays visible when scrolling the table body */
.insight-records-modal__table-wrapper ::v-deep .table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;
    box-shadow: 0 1px 0 0 #dee2e6;
}

.insight-records-modal__table-wrapper ::v-deep > div > .row {
    flex-shrink: 0;
    margin-top: 0.5rem;
}

.insight-records-modal__empty {
    padding: 2rem 1rem;
    margin: 0;
    font-style: italic;
    color: #6c757d;
    text-align: center;
}
</style>
